import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const GOLD = '#FFC220';

const CATEGORIES = [
  'On-Campus',
  'Convenience Stores',
  'Gas Stations',
  'Grocery Stores',
  'Restaurants',
];

const emptyForm = {
  name: '',
  category: 'Restaurants',
  address: '',
  city: 'Boulder',
  state: 'CO',
  zip: '',
  phone: '',
  logo_url: '',
  active_fmp: true,
  active_athletics: false,
};

export default function Merchants() {
  const [merchants, setMerchants] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');

  useEffect(() => {
    fetchMerchants();
  }, []);

  useEffect(() => {
    let results = merchants;

    if (categoryFilter !== 'All') {
      results = results.filter((m) => m.category === categoryFilter);
    }

    if (search) {
      const lower = search.toLowerCase();
      results = results.filter(
        (m) =>
          m.name.toLowerCase().includes(lower) ||
          (m.address && m.address.toLowerCase().includes(lower))
      );
    }

    setFiltered(results);
  }, [search, categoryFilter, merchants]);

  const fetchMerchants = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('merchants')
      .select('*')
      .order('name', { ascending: true });

    if (!error) {
      setMerchants(data);
      setFiltered(data);
    }
    setLoading(false);
  };

  const handleToggle = async (merchant, field) => {
    await supabase
      .from('merchants')
      .update({ [field]: !merchant[field] })
      .eq('id', merchant.id);
    fetchMerchants();
  };

  const handleEdit = (merchant) => {
    setForm({
      name: merchant.name || '',
      category: merchant.category || 'Restaurants',
      address: merchant.address || '',
      city: merchant.city || 'Boulder',
      state: merchant.state || 'CO',
      zip: merchant.zip || '',
      phone: merchant.phone || '',
      logo_url: merchant.logo_url || '',
      active_fmp: merchant.active_fmp,
      active_athletics: merchant.active_athletics,
    });
    setEditingId(merchant.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this merchant? This cannot be undone.')) return;
    await supabase.from('merchants').delete().eq('id', id);
    fetchMerchants();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category) {
      alert('Please enter a merchant name and category.');
      return;
    }

    setSaving(true);

    if (editingId) {
      await supabase
        .from('merchants')
        .update(form)
        .eq('id', editingId);
      setSuccessMsg('Merchant updated successfully!');
    } else {
      await supabase
        .from('merchants')
        .insert(form);
      setSuccessMsg('Merchant added successfully!');
    }

    setTimeout(() => setSuccessMsg(''), 3000);
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
    setSaving(false);
    fetchMerchants();
  };

  const handleCancel = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) return (
    <div style={styles.loading}><p style={{ color: GOLD }}>Loading...</p></div>
  );

  return (
    <div style={styles.wrap}>

      {/* TOPBAR */}
      <div style={styles.topbar}>
        <h1 style={styles.topbarTitle}>
          Merchants
          <span style={styles.topbarCount}>{filtered.length} of {merchants.length}</span>
        </h1>
        <button
          style={styles.topbarBtn}
          onClick={() => { setShowForm(!showForm); setEditingId(null); setForm(emptyForm); }}
        >
          {showForm && !editingId ? '✕ Cancel' : '+ Add Merchant'}
        </button>
      </div>

      <div style={styles.content}>

        {/* SUCCESS */}
        {successMsg && (
          <div style={styles.successBanner}>✅ {successMsg}</div>
        )}

        {/* FORM */}
        {showForm && (
          <div style={styles.formCard}>
            <h2 style={styles.formTitle}>
              {editingId ? 'Edit Merchant' : 'Add New Merchant'}
            </h2>
            <form onSubmit={handleSubmit}>

              <div style={styles.formGrid}>

                <div style={styles.formRow}>
                  <label style={styles.label}>MERCHANT NAME *</label>
                  <input
                    style={styles.input}
                    type="text"
                    placeholder="e.g. Aviano Coffee"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>

                <div style={styles.formRow}>
                  <label style={styles.label}>CATEGORY *</label>
                  <select
                    style={styles.select}
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    required
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div style={styles.formRow}>
                  <label style={styles.label}>STREET ADDRESS</label>
                  <input
                    style={styles.input}
                    type="text"
                    placeholder="e.g. 1253 Pleasant Street"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                  />
                </div>

                <div style={styles.formRow}>
                  <label style={styles.label}>PHONE</label>
                  <input
                    style={styles.input}
                    type="text"
                    placeholder="e.g. 303.555.1234"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>

                <div style={styles.formRow}>
                  <label style={styles.label}>CITY</label>
                  <input
                    style={styles.input}
                    type="text"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                  />
                </div>

                <div style={styles.formRow}>
                  <label style={styles.label}>ZIP CODE</label>
                  <input
                    style={styles.input}
                    type="text"
                    placeholder="e.g. 80302"
                    value={form.zip}
                    onChange={(e) => setForm({ ...form, zip: e.target.value })}
                  />
                </div>

                <div style={{ ...styles.formRow, gridColumn: '1 / -1' }}>
                  <label style={styles.label}>LOGO URL</label>
                  <input
                    style={styles.input}
                    type="text"
                    placeholder="https://... (paste Supabase storage URL)"
                    value={form.logo_url}
                    onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                  />
                  {form.logo_url && (
                    <img
                      src={form.logo_url}
                      alt="Logo preview"
                      style={styles.logoPreview}
                    />
                  )}
                </div>

              </div>

              <div style={styles.formRow}>
                <label style={styles.label}>ACTIVE IN</label>
                <div style={styles.checkRow}>
                  <label style={styles.checkLabel}>
                    <input
                      type="checkbox"
                      checked={form.active_fmp}
                      onChange={(e) => setForm({ ...form, active_fmp: e.target.checked })}
                      style={{ marginRight: '8px' }}
                    />
                    FMP App
                  </label>
                  <label style={styles.checkLabel}>
                    <input
                      type="checkbox"
                      checked={form.active_athletics}
                      onChange={(e) => setForm({ ...form, active_athletics: e.target.checked })}
                      style={{ marginRight: '8px' }}
                    />
                    Gold Card App
                  </label>
                </div>
              </div>

              <div style={styles.formBtns}>
                <button type="submit" style={styles.submitBtn} disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Merchant'}
                </button>
                <button type="button" style={styles.cancelBtn} onClick={handleCancel}>
                  Cancel
                </button>
              </div>

            </form>
          </div>
        )}

        {/* FILTERS */}
        <div style={styles.filterRow}>
          <input
            style={styles.searchInput}
            type="text"
            placeholder="Search merchants..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div style={styles.categoryFilters}>
            {['All', ...CATEGORIES].map((cat) => (
              <button
                key={cat}
                style={{
                  ...styles.catBtn,
                  ...(categoryFilter === cat ? styles.catBtnActive : {}),
                }}
                onClick={() => setCategoryFilter(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* TABLE */}
        <div style={styles.tableWrap}>
          {filtered.length === 0 ? (
            <div style={styles.empty}>No merchants found.</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Logo', 'Name', 'Category', 'Address', 'Phone', 'FMP', 'Gold Card', 'Actions'].map((h) => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr key={m.id} style={styles.tr}>
                    <td style={styles.td}>
                      {m.logo_url ? (
                        <img
                          src={m.logo_url}
                          alt={m.name}
                          style={styles.tableLogoImg}
                        />
                      ) : (
                        <div style={styles.tableLogoPlaceholder}>
                          <span style={styles.tableLogoInitials}>
                            {m.name.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase()}
                          </span>
                        </div>
                      )}
                    </td>
                    <td style={{ ...styles.td, fontWeight: '500' }}>{m.name}</td>
                    <td style={styles.td}>
                      <span style={styles.catBadge}>{m.category}</span>
                    </td>
                    <td style={{ ...styles.td, color: '#888', fontSize: '12px' }}>
                      {m.address ? `${m.address}, ${m.city}` : '—'}
                    </td>
                    <td style={{ ...styles.td, color: '#888', fontSize: '12px' }}>
                      {m.phone || '—'}
                    </td>
                    <td style={styles.td}>
                      <Toggle
                        value={m.active_fmp}
                        onChange={() => handleToggle(m, 'active_fmp')}
                      />
                    </td>
                    <td style={styles.td}>
                      <Toggle
                        value={m.active_athletics}
                        onChange={() => handleToggle(m, 'active_athletics')}
                      />
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionBtns}>
                        <button style={styles.editBtn} onClick={() => handleEdit(m)}>
                          Edit
                        </button>
                        <button style={styles.deleteBtn} onClick={() => handleDelete(m.id)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <div
      onClick={onChange}
      style={{
        width: '36px',
        height: '20px',
        borderRadius: '10px',
        background: value ? GOLD : '#ddd',
        position: 'relative',
        cursor: 'pointer',
        transition: 'background 0.2s',
        flexShrink: 0,
      }}
    >
      <div style={{
        position: 'absolute',
        width: '14px',
        height: '14px',
        borderRadius: '50%',
        background: '#fff',
        top: '3px',
        left: value ? '19px' : '3px',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' },
  topbar: {
    background: '#fff',
    borderBottom: '1px solid #e0e0e0',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  topbarTitle: {
    fontSize: '18px',
    fontWeight: '500',
    color: '#111',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  topbarCount: {
    fontSize: '13px',
    fontWeight: '400',
    color: '#aaa',
  },
  topbarBtn: {
    background: GOLD,
    color: '#111',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  content: { padding: '24px', flex: 1 },
  successBanner: {
    background: '#f0fff4',
    border: '1px solid #86efac',
    borderRadius: '10px',
    padding: '12px 16px',
    marginBottom: '20px',
    color: '#166534',
    fontSize: '14px',
  },
  formCard: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '14px',
    padding: '24px',
    marginBottom: '24px',
  },
  formTitle: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#111',
    marginBottom: '20px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '16px',
  },
  formRow: { marginBottom: '16px' },
  label: {
    display: 'block',
    color: '#888',
    fontSize: '10px',
    letterSpacing: '1px',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#111',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#111',
    fontFamily: 'inherit',
    outline: 'none',
    background: '#fff',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  logoPreview: {
    width: '60px',
    height: '60px',
    objectFit: 'contain',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    marginTop: '8px',
    background: '#f9f9f9',
  },
  checkRow: {
    display: 'flex',
    gap: '24px',
  },
  checkLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    color: '#444',
    cursor: 'pointer',
  },
  formBtns: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px',
  },
  submitBtn: {
    background: GOLD,
    color: '#111',
    border: 'none',
    borderRadius: '10px',
    padding: '11px 24px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  cancelBtn: {
    background: 'none',
    color: '#888',
    border: '1px solid #e0e0e0',
    borderRadius: '10px',
    padding: '11px 24px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  filterRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  searchInput: {
    padding: '9px 14px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#111',
    fontFamily: 'inherit',
    outline: 'none',
    width: '240px',
  },
  categoryFilters: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  },
  catBtn: {
    background: '#f5f5f5',
    border: '1px solid #e0e0e0',
    borderRadius: '20px',
    padding: '5px 12px',
    fontSize: '12px',
    color: '#666',
    cursor: 'pointer',
  },
  catBtnActive: {
    background: GOLD,
    border: `1px solid ${GOLD}`,
    color: '#111',
    fontWeight: '500',
  },
  tableWrap: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    background: '#f9f9f9',
    color: '#888',
    fontSize: '11px',
    padding: '10px 16px',
    textAlign: 'left',
    borderBottom: '1px solid #e0e0e0',
    fontWeight: '500',
    whiteSpace: 'nowrap',
  },
  tr: {
    borderBottom: '1px solid #f0f0f0',
  },
  td: {
    padding: '10px 16px',
    color: '#111',
    verticalAlign: 'middle',
  },
  tableLogoImg: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    objectFit: 'contain',
    border: '1px solid #e0e0e0',
    background: '#fff',
  },
  tableLogoPlaceholder: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    background: '#111',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tableLogoInitials: {
    color: GOLD,
    fontSize: '10px',
    fontWeight: '600',
  },
  catBadge: {
    background: '#f0f0f0',
    color: '#666',
    fontSize: '11px',
    padding: '3px 8px',
    borderRadius: '20px',
    whiteSpace: 'nowrap',
  },
  actionBtns: {
    display: 'flex',
    gap: '6px',
  },
  editBtn: {
    background: 'none',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '12px',
    color: '#444',
    cursor: 'pointer',
  },
  deleteBtn: {
    background: 'none',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '12px',
    color: '#ff6b6b',
    cursor: 'pointer',
  },
  empty: {
    padding: '32px',
    textAlign: 'center',
    color: '#888',
    fontSize: '13px',
  },
};